import { Text, View, ScrollView, TouchableOpacity, RefreshControl, Linking } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Animated, { FadeIn, FadeInDown, FadeInUp, Layout } from 'react-native-reanimated';
import Toast from 'react-native-toast-message';
import * as Crypto from 'expo-crypto';
import { updateAssignees, updateAssigneeResponse, updateTaskCompletionStatus, getAssigneesNeeded, getConflictingTaskTitles } from '@/data/tasks';
import { isAutomationDueType, isFollowUpDueType } from '@/utils/constants/taskTypes';
import { getTaskPurposeIcon, getTaskPurposeLabel } from '@/utils/constants/followUp';
import { PREDEFINED_TASK_TYPES } from '@/components/Paths/flow/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Mutation } from '@/utils/react-query/Mutations';
import CompleteCareTaskSheet from '@/components/FollowUp/CompleteCareTaskSheet';
import Callout from '@/components/General/Callout';
import Chip from '@/components/General/Chip';
import ProfileCard from '@/components/People/ProfileCard';
import Avatar from '@/components/People/Avatar';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SolidButton from '@/components/General/SolidButton';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Input from '@/components/General/inputs/Input';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { invalidateServiceFlowForTaskInstance } from '@/utils/serviceFlow';
import InputSection from '@/components/General/inputs/InputSection';
import SectionTitle from '@/components/General/SectionTitle';
import SheetModal from '@/components/General/SheetModal';
import ContentLoader from '@/components/General/ContentLoader';
import { useLocalSearchParams, useRouter, useNavigation, usePathname } from 'expo-router';
import Unicon from '@/components/General/Unicon';
import PageTitle from '@/components/General/PageTitle';
import { getAccessibleTextColor } from '@/utils/colorAccessibility';
import useMarkNotificationsRead from '@/utils/hooks/useMarkNotificationsRead';
import useTaskInstanceLocation from '@/utils/hooks/useTaskInstanceLocation';

const hasDisplayValue = (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
};

const formatProfileFieldValue = (value, type) => {
    if (!hasDisplayValue(value)) return null;
    if (type === 'Checkbox' || typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (type === 'Date') {
        const dateOnlyMatch = typeof value === 'string' && value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const date = dateOnlyMatch
            ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
            : new Date(value);
        return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
    }
    if (type === 'File Upload') {
        const files = Array.isArray(value) ? value : [value];
        return files.map(file => typeof file === 'string' ? file : file?.filename || file?.name).filter(Boolean).join(', ');
    }
    if (Array.isArray(value)) {
        return value.map(item => typeof item === 'object' ? item?.label || item?.name || item?.value : item).filter(hasDisplayValue).join(', ');
    }
    if (typeof value === 'object') return value.label || value.name || value.value || null;
    return String(value);
};

const TaskInstanceDetailsScreen = () => {
    const [currentUserAssignment, setCurrentUserAssignment] = useState(false);
    const [taskNotFound, setTaskNotFound] = useState(false);
    const [declineModalOpen, setDeclineModalOpen] = useState(false);
    const [denyModalOpen, setDenyModalOpen] = useState(false);
    const [denyReason, setDenyReason] = useState('');
    const [decidedApproval, setDecidedApproval] = useState(null);
    const [careSheetOpen, setCareSheetOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const { taskInstanceID } = useLocalSearchParams();
    const router = useRouter();
    const navigation = useNavigation();
    const pathname = usePathname();
    const scroll_ref = useRef();

    if (!taskInstanceID) {
        setTaskNotFound(true);
    }

    const queryClient = useQueryClient();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: currentTaskInstance, isLoading: isLoadingTaskInstance, isFetching } = Query.taskInstances.byId({ id: taskInstanceID });
    const { data: recentAssignees } = Query.tasks.recentAssignees({
        taskID: currentTaskInstance?.taskID,
        options: { enabled: !!currentTaskInstance?.taskID },
    });
    const { data: assigneeTeam } = Query.taskTeams.byId({
        id: currentTaskInstance?.VolunteerTeam?.id,
        options: { enabled: !!currentTaskInstance?.VolunteerTeam?.id },
    });
    // Automation enrollment context for this instance (the PathEnrollmentSteps
    // link) — also the REQUEST_APPROVAL discriminator via the step actionType.
    const { data: enrollmentContext } = Query.paths.enrollmentContext({ taskInstanceID });
    const locationName = useTaskInstanceLocation(currentTaskInstance);
    const pathEnrollment = currentTaskInstance?.PathEnrollmentSteps?.[0]?.PathEnrollment;
    const pathEnrollee = pathEnrollment?.Person;
    const canViewBasicInfo = loggedInUser?.isAdmin || loggedInUser?.can?.('View Basic Info');
    const canViewContactInfo = loggedInUser?.isAdmin || loggedInUser?.can?.('View Contact Info');
    const canViewCustomFields = loggedInUser?.isAdmin || loggedInUser?.can?.('View Custom Fields');
    const canViewEnrolleeFields = canViewBasicInfo || canViewContactInfo || canViewCustomFields;
    const isPathTask = isAutomationDueType(currentTaskInstance?.Task?.dueType);
    // Approval tasks are Individual tasks spawned by a v2 REQUEST_APPROVAL
    // step — detect via the embedded enrollment-step link (or the context
    // query's step actionType as a fallback).
    const isApprovalTask = currentTaskInstance?.PathEnrollmentSteps?.[0]?.PathStep?.actionType === 'REQUEST_APPROVAL'
        || enrollmentContext?.PathStep?.actionType === 'REQUEST_APPROVAL';
    const hasAutomationContext = isPathTask || isApprovalTask;
    const { data: pathEnrolleeProfile } = Query.people.byId({
        id: pathEnrollee?.id,
        options: { enabled: hasAutomationContext && !!pathEnrollee?.id && !!canViewEnrolleeFields },
    });
    const { data: personCustomFieldSections } = Query.personCustomFieldSections.all({
        options: { enabled: hasAutomationContext && !!canViewCustomFields },
    });

    // Care-task probe (P4): returns the helper-safe follow_up_get_assigned_task
    // projection for care instances, null for everything else — null is a
    // state, not an error, so non-care tasks never see a probe failure.
    const { data: careTask } = Query.followUp.assignedTask({
        organizationID: loggedInUser?.organizationID,
        taskInstanceID,
        options: { enabled: !!loggedInUser?.organizationID && !!taskInstanceID },
    });
    const isCareTask = !!careTask;
    const isCancelled = !!(currentTaskInstance?.cancelledAt || careTask?.cancelledAt);
    const canViewFollowUp = !!loggedInUser?.can?.('View Follow Up');

    const pathStep = enrollmentContext?.PathStep;
    const pathInfo = enrollmentContext?.PathEnrollment?.Path;
    const canViewPath = loggedInUser?.isAdmin || loggedInUser?.can?.("Update Paths");

    // Care-task completion goes through follow_up_complete_task — the server
    // rejects direct completed=true writes with
    // FOLLOW_UP_COMPLETION_VIA_RPC_REQUIRED. MEAL/APPROVAL complete with a
    // purpose outcome; OUTREACH/OTHER open the structured completion sheet.
    const completeCareTaskMutation = Mutation.followUp.completeTask({
        onSuccess: (data) => {
            Toast.show({
                type: 'success',
                text2: data?.touchID
                    ? (data?.isConnected ? 'Task completed — connected' : 'Task completed — attempt recorded')
                    : 'Task completed',
            });
            resetQueries();
        },
        onError: (error) => {
            if (error?.followUpCode === 'FOLLOW_UP_CONFLICT') {
                // Concurrent completion/cancellation — show the new state.
                Toast.show({ type: 'info', text2: 'This task was already completed or cancelled.' });
                resetQueries();
                return;
            }
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to complete the task. Please try again.' });
            }
        },
    });

    // Care-task (re)assignment goes through follow_up_assign_task — direct
    // TaskInstancePeople writes can't bypass claim/assignment rules (§10.5).
    const assignCareTaskMutation = Mutation.followUp.assignTask({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Task assigned' });
            resetQueries();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to assign the task. Please try again.' });
            }
        },
    });

    // REQUEST_APPROVAL decision via automation_record_approval — first
    // approver response wins; {already:true} means someone else decided.
    const recordApprovalMutation = Mutation.automations.recordApproval({
        onSuccess: (data) => {
            if (data?.already) {
                Toast.show({ type: 'info', text2: 'Another approver already decided — showing the recorded outcome.' });
                resetQueries();
                return;
            }
            const decision = data?.decision || null;
            setDecidedApproval(decision);
            Toast.show({ type: 'success', text2: decision === 'APPROVED' ? 'Approved!' : 'Denied.' });
            resetQueries();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to record your decision. Please try again.' });
            }
        },
    });

    const completionStatusMutation = useMutation({
        mutationFn: updateTaskCompletionStatus,
        onMutate: (data) => {
            const newTaskInstance = { ...currentTaskInstance };
            newTaskInstance.completed = data.completed;
            return { newTaskInstance };
        },
        onSuccess: (data, variables, context) => {
            router.setParams({ taskInstance: context.newTaskInstance });
            resetQueries();
        }
    });

    const updateAssigneeResponseMutation = useMutation({
        mutationFn: updateAssigneeResponse,
        onSuccess: (data) => {
            if(data) Toast.show({ type: 'success', text2: 'Assignees have been updated!' });
            resetQueries();
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to send your response... Please try again later' });
        },
    });

    const updateAssigneesMutation = useMutation({
        mutationFn: updateAssignees,
        onSuccess: (data) => {
            const newTaskInstance = { ...currentTaskInstance };
            newTaskInstance.Assignees = data;
            data.forEach(a => {
                queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(a.personID) });
            });
            router.setParams({ taskInstance: newTaskInstance });
            resetQueries();
            Toast.show({ type: 'success', text2: 'Assignees have been updated!' });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to update assignees. Please try again.' });
        }
    });

    const resetQueries = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.byId(currentTaskInstance?.Task?.id) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(currentTaskInstance?.id) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(currentTaskInstance?.EventInstance?.id) });
        if (currentTaskInstance?.EventInstance?.eventID) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(currentTaskInstance.EventInstance.eventID) });
        }
        queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(loggedInUser?.id) });
        if (loggedInUser?.id) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.personHistory(loggedInUser.id) });
        }
        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTasks(loggedInUser?.id) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTaskAssignments(loggedInUser?.id) });
        queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
        queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.individual() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.paths.enrollmentContext(taskInstanceID) });
        // Care-task views: the probe result, the context person's open-task
        // list, and the manager's Needs Owner queue all reflect completion/
        // cancellation/assignment changes.
        if (loggedInUser?.organizationID) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.assignedTask(loggedInUser.organizationID, taskInstanceID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.needsOwner(loggedInUser.organizationID) });
            if (careTask?.personID) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.person(loggedInUser.organizationID, careTask.personID) });
                queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.personTasks(loggedInUser.organizationID, careTask.personID) });
            }
        }
        // Parent Service Flow: task rows are nested inside the flow query —
        // assignment/response/completion changes also update the date's
        // protection status (spec §12.5).
        invalidateServiceFlowForTaskInstance(queryClient, {
            eventInstanceID: currentTaskInstance?.eventInstanceID ?? currentTaskInstance?.EventInstance?.id,
            organizationID: loggedInUser?.organizationID,
            accountID: loggedInUser?.accountID,
        });
    }, [queryClient, currentTaskInstance, loggedInUser, careTask, taskInstanceID]);

    const handleRefresh = useCallback(() => {
        setRefreshing(true);
        queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(taskInstanceID) });
    }, [queryClient, taskInstanceID]);

    useEffect(() => {
        if (!isFetching) setRefreshing(false);
    }, [isFetching]);

    // Scoped to THIS task instance: TASK_RESPONSE carries parameters.taskInstanceID,
    // TASK_ASSIGNED doesn't — the hook falls back to matching the notification path.
    useMarkNotificationsRead({ taskInstanceID, types: ["TASK_ASSIGNED", "TASK_RESPONSE"] });

    useEffect(() => {
        const isAssigner = currentTaskInstance?.VolunteerTeam?.VolunteerTeamAssigners?.find(a => a.personID === loggedInUser?.id);
        // Path task instances have no volunteer team — path editors reassign.
        const isPathEditor = isAutomationDueType(currentTaskInstance?.Task?.dueType) && loggedInUser?.can?.("Update Paths");
        const isAssignee = currentTaskInstance?.Assignees?.find(a => a.id === loggedInUser?.id);
        setCurrentUserAssignment(isAssignee);

        navigation.setOptions({
            headerTitle: () => (
                <PageTitle title={currentTaskInstance?.Task?.title} subtitle={locationName ? `Task Occurrence · ${locationName}` : 'Task Occurrence'} />
            ),
            headerRight: () => (
                <View style={styles.headerRight}>
                    {isCareTask ? (
                        // Care tasks assign one person via follow_up_assign_task
                        // (manage or primary caregiver — server enforces). The
                        // generic multi-assignee writer is never used.
                        canViewFollowUp && !isCancelled && !assignCareTaskMutation.isPending && (
                            <SheetSelector
                                iconSize={30}
                                forceModal
                                addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null}
                                type="people"
                                name="Assign to"
                                fromScreen={pathname}
                                onChange={(selected) => {
                                    if (!selected?.id) return;
                                    assignCareTaskMutation.mutate({
                                        organizationID: loggedInUser?.organizationID,
                                        taskInstanceID: currentTaskInstance?.id,
                                        assigneePersonID: selected.id,
                                        personID: careTask?.personID,
                                    });
                                }}
                            >
                                <Unicon name="person-add-outline" size={22} colorVariant="text" />
                            </SheetSelector>
                        )
                    ) : (loggedInUser?.isAdmin || isAssigner || isPathEditor || currentTaskInstance?.Task?.dueType === 'INDIVIDUAL') && (
                        <SheetSelector
                            iconSize={30}
                            multiple
                            forceModal
                            addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null}
                            type="people"
                            name="Assignees"
                            fromScreen={pathname}
                            recents={recentAssignees}
                            peopleSubset={assigneeTeam?.Members}
                            subsetLabel={currentTaskInstance?.VolunteerTeam?.name}
                            value={currentTaskInstance?.Assignees || []}
                            errorFn={(item) => {
                                const errors = [];
                                if (item.BlackoutPeriods?.length > 0) {
                                    const isInBlackoutPeriod = item.BlackoutPeriods?.some(b => {
                                        const due = new Date(currentTaskInstance?.dueDate);
                                        return due >= new Date(b.startDate) && due <= new Date(b.endDate);
                                    });
                                    if (isInBlackoutPeriod) errors.push("This date is blacked out");
                                }
                                const conflicts = getConflictingTaskTitles(currentTaskInstance, item.id);
                                if (conflicts.length > 0) errors.push(`Scheduling conflict with ${conflicts.join(', ')}`);
                                return errors;
                            }}
                            onChange={(selected_items) => {
                                updateAssigneesMutation.mutate({
                                    taskInstanceID: currentTaskInstance?.id,
                                    existingAssignees: currentTaskInstance?.Assignees,
                                    newAssignees: selected_items,
                                    taskInstance: currentTaskInstance,
                                    currentUser: loggedInUser?.id
                                });
                            }}
                        >
                            <Unicon name="person-add-outline" size={22} colorVariant="text" />
                        </SheetSelector>
                    )}
                </View>
            ),
        });
    }, [navigation, currentTaskInstance, loggedInUser, styles, pathname, locationName, recentAssignees, assigneeTeam, isCareTask, canViewFollowUp, isCancelled, careTask, assignCareTaskMutation]);

    const status = useMemo(() => {
        // Cancelled is its own terminal state — never folds into Completed.
        if (isCancelled) return "Cancelled";
        if (currentTaskInstance?.Assignees?.length === 0) return "Unassigned";
        if (currentTaskInstance?.completed || careTask?.completed) return "Completed";
        return "Assigned";
    }, [currentTaskInstance, careTask, isCancelled]);

    const now = new Date();
    const isOverdue = !isCancelled && !currentTaskInstance?.Task?.assumeCompleted && !currentTaskInstance?.completed && currentTaskInstance?.dueDate < now.toISOString();

    const { data: trainingClassInstance } = Query.trainingClassInstances.byTaskInstanceId({
        id: taskInstanceID,
        options: { enabled: currentTaskInstance?.Task?.dueType === 'TRAINING' }
    });

    const taskDate = useMemo(() => {
        const dueType = currentTaskInstance?.Task?.dueType;
        let dateValue = currentTaskInstance?.EventInstance?.startTime;
        if (dueType === "INDIVIDUAL" || isAutomationDueType(dueType) || isFollowUpDueType(dueType)) dateValue = currentTaskInstance?.dueDate ?? careTask?.dueDate;
        if (dueType === "TRAINING") dateValue = trainingClassInstance?.scheduledAt ?? currentTaskInstance?.dueDate;
        if (!dateValue) return null;

        const date = new Date(dateValue);
        return Number.isNaN(date.getTime()) ? null : date;
    }, [currentTaskInstance, trainingClassInstance, careTask]);

    const eventName = currentTaskInstance?.EventInstance?.Event?.name;
    const eventColor = currentTaskInstance?.EventInstance?.Event?.Calendar?.color;

    const getStatusConfig = useCallback(() => {
        if (isOverdue) return { label: "Overdue", colorVariant: "error", textColorVariant: "onError", icon: "alert-circle" };
        switch (status) {
            case "Cancelled": return { label: "Cancelled", colorVariant: "textSecondary", textColorVariant: "text", icon: "close-circle" };
            case "Completed": return { label: "Completed", colorVariant: "success", textColorVariant: "onSuccess", icon: "checkmark-circle" };
            case "Assigned": return { label: "Assigned", colorVariant: "warning", textColorVariant: "onWarning", icon: "time" };
            default: return { label: "Unassigned", colorVariant: "textSecondary", textColorVariant: "text", icon: "person-outline" };
        }
    }, [status, isOverdue]);

    const getCurrentUserStatusConfig = useCallback(() => {
        if (currentUserAssignment?.assignmentResponse === "ACCEPTED") return { label: "You Accepted This Task", textColorVariant: "onSuccess", colorVariant: "success", icon: "checkmark-circle" };
        if (currentUserAssignment?.assignmentResponse === "DECLINED") return { label: "You Declined This Task", textColorVariant: "onError", colorVariant: "error", icon: "alert-circle" };
        return { label: "Awaiting Your Response", textColorVariant: "onWarning", colorVariant: "warning", icon: "time" };
    }, [currentUserAssignment]);

    const getTaskTypeLabel = useCallback((dueType) => {
        switch (dueType) {
            case "INDIVIDUAL": return "Individual Task";
            case "EVENT_VOLUNTEER": return "Event Volunteer";
            case "TRAINING": return "Training";
            case "PATHS":
            case "AUTOMATIONS": return "Automations";
            case "FOLLOW_UP": return "Follow Up";
            default: return "Service Flow";
        }
    }, []);

    const updateAssignment = useCallback((personID, newResponse, reason = null, forwardToID = null) => {
        if (newResponse === 'DECLINED' && reason === null) {
            Toast.show({ type: "error", text2: "You must provide a reason to decline a task" });
            return false;
        }
        updateAssigneeResponseMutation.mutate({
            personID,
            taskInstanceID: currentTaskInstance?.id,
            newResponse,
            reason,
            forwardToID,
            loggedInUserID: loggedInUser?.id
        });
    }, [currentTaskInstance, loggedInUser, updateAssigneeResponseMutation]);

    /**
     * Care-task completion, routed by purpose (spec §10.3):
     * - OUTREACH/OTHER open the structured sheet (touch required/optional).
     * - MEAL records 'DELIVERED', APPROVAL records 'APPROVED' — a delivery or
     *   approval fact, never a fabricated conversation.
     * Un-completing care work has no RPC — there is no "mark incomplete" path.
     */
    const handleCareComplete = useCallback(() => {
        const purpose = careTask?.purpose || 'OTHER';
        if (purpose === 'OUTREACH' || purpose === 'OTHER') {
            setCareSheetOpen(true);
            return;
        }
        const outcome = purpose === 'MEAL' ? 'DELIVERED' : 'APPROVED';
        completeCareTaskMutation.mutate({
            organizationID: loggedInUser?.organizationID,
            taskInstanceID: currentTaskInstance?.id,
            personID: careTask?.personID,
            loggedInPersonID: loggedInUser?.id,
            payload: {
                taskInstanceID: currentTaskInstance?.id,
                outcome,
                idempotencyKey: Crypto.randomUUID(),
            },
        });
    }, [careTask, currentTaskInstance, completeCareTaskMutation, loggedInUser]);

    // REQUEST_APPROVAL: the approver's decision IS the task response — it
    // resolves the parked automation step (APPROVED|DENIED). Approve sends no
    // note; Deny collects an optional reason in the deny sheet.
    const handleApprove = useCallback(() => {
        if (recordApprovalMutation.isPending || decidedApproval) return;
        recordApprovalMutation.mutate({
            taskInstanceID: currentTaskInstance?.id,
            decision: 'APPROVED',
            loggedInPersonID: loggedInUser?.id,
        });
    }, [currentTaskInstance, loggedInUser, recordApprovalMutation, decidedApproval]);

    const handleDeny = useCallback(() => {
        if (recordApprovalMutation.isPending || decidedApproval) return;
        recordApprovalMutation.mutate({
            taskInstanceID: currentTaskInstance?.id,
            decision: 'DENIED',
            note: denyReason?.trim() || null,
            loggedInPersonID: loggedInUser?.id,
        });
        setDenyModalOpen(false);
        setDenyReason('');
    }, [currentTaskInstance, loggedInUser, denyReason, recordApprovalMutation, decidedApproval]);

    const updateCompletion = useCallback((completed) => {
        // Care tasks complete ONLY through follow_up_complete_task — the
        // server rejects the direct write anyway, so route rather than fail.
        if (isCareTask) {
            if (completed) handleCareComplete();
            return;
        }
        completionStatusMutation.mutate({ id: currentTaskInstance?.id, completed });
    }, [currentTaskInstance, completionStatusMutation, isCareTask, handleCareComplete]);

    const sortedAssignees = useMemo(() => {
        return currentTaskInstance?.Assignees?.sort((a, b) => {
            let aValue = (a?.assignmentResponse === "DECLINED" ? -2 : 0) + (a.position < b.position ? 1 : 0);
            let bValue = (b?.assignmentResponse === "DECLINED" ? -2 : 0) + (b.position < a.position ? 1 : 0);
            return bValue - aValue;
        }) || [];
    }, [currentTaskInstance?.Assignees]);

    const enrolleeProfile = pathEnrolleeProfile?.id ? pathEnrolleeProfile : pathEnrollee;
    const enrolleeFields = useMemo(() => {
        if (!enrolleeProfile) return [];

        const fields = [];
        if (canViewBasicInfo) {
            if (hasDisplayValue(enrolleeProfile.gender)) fields.push({ label: 'Gender', value: enrolleeProfile.gender });
            if (hasDisplayValue(enrolleeProfile.dateOfBirth)) {
                fields.push({ label: 'Birthday', value: formatProfileFieldValue(enrolleeProfile.dateOfBirth, 'Date') });
            }
        }
        if (canViewContactInfo) {
            if (hasDisplayValue(enrolleeProfile.email)) fields.push({ label: 'Email', value: enrolleeProfile.email });
            (enrolleeProfile.Phones || []).forEach((phone, index) => {
                if (hasDisplayValue(phone.phoneNumber)) fields.push({ label: phone.label || `Phone ${index + 1}`, value: phone.phoneNumber });
            });
            const address = enrolleeProfile.Address;
            if (address) {
                const street = [address.street, address.streetLine2].filter(hasDisplayValue).join(', ');
                const locality = [address.city, address.state, address.zip].filter(hasDisplayValue).join(' ');
                const addressValue = [street, locality].filter(hasDisplayValue).join(', ');
                if (addressValue) fields.push({ label: 'Mailing Address', value: addressValue });
            }
        }
        if (canViewCustomFields) {
            (personCustomFieldSections || []).forEach(section => {
                (section.CustomFields || []).forEach(field => {
                    const rawValue = enrolleeProfile.customFieldValues?.[field.id];
                    if (!hasDisplayValue(rawValue)) return;
                    const value = formatProfileFieldValue(rawValue, field.type);
                    if (hasDisplayValue(value)) fields.push({ label: field.name, value, section: section.name });
                });
            });
        }
        return fields;
    }, [enrolleeProfile, canViewBasicInfo, canViewContactInfo, canViewCustomFields, personCustomFieldSections]);

    const statusConfig = getStatusConfig();
    const currentUserStatusConfig = getCurrentUserStatusConfig();

    const isCompleting = completionStatusMutation.isPending || completeCareTaskMutation.isPending;
    const isResponding = updateAssigneeResponseMutation.isPending;
    const isAssigning = updateAssigneesMutation.isPending || assignCareTaskMutation.isPending;
    const isDeciding = recordApprovalMutation.isPending;
    const isActionPending = isCompleting || isResponding || isAssigning || isDeciding;

    // Recorded approval outcome: the RPC result wins until the instance
    // refetch lands the server-stamped approvalOutcome columns.
    const approvalOutcome = decidedApproval || currentTaskInstance?.approvalOutcome || null;
    const approvalDecider = currentTaskInstance?.approvalDecidedByPersonID
        ? currentTaskInstance?.Assignees?.find(a => a.id === currentTaskInstance.approvalDecidedByPersonID)
            || (currentTaskInstance.approvalDecidedByPersonID === loggedInUser?.id ? loggedInUser : null)
        : (decidedApproval ? loggedInUser : null);
    const approvalDeciderName = approvalDecider
        ? `${approvalDecider.firstName || ''} ${approvalDecider.lastName || ''}`.trim()
        : null;
    const approvalDecidedAt = currentTaskInstance?.approvalDecidedAt
        ? new Date(currentTaskInstance.approvalDecidedAt).toLocaleString('default', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : null;

    const renderLoadingState = () => (
        <View style={styles.loadingContainer}>
            <ContentLoader containerHeight={180} borderRadius={20} />
            <View style={styles.loadingRow}>
                <ContentLoader containerHeight={100} borderRadius={16} style={styles.loadingHalf} staggerPosition={1} />
                <ContentLoader containerHeight={100} borderRadius={16} style={styles.loadingHalf} staggerPosition={2} />
            </View>
            <ContentLoader containerHeight={120} borderRadius={16} staggerPosition={3} />
        </View>
    );

    const renderNotFound = () => (
        <View style={styles.notFoundContainer}>
            <Unicon name="alert-circle-outline" size={64} colorVariant="textSecondary" />
            <Text style={styles.notFoundTitle}>Task not found</Text>
            <Text style={styles.notFoundSubtitle}>This task has been deleted, or does not exist</Text>
        </View>
    );

    const renderContent = () => (
        <Animated.View entering={FadeIn.duration(300)} style={styles.contentContainer}>
            {/* Status Hero Card */}
            <Animated.View entering={FadeInDown.delay(100).duration(400)} style={styles.heroCard}>
                <Text style={styles.heroTitle}>{currentTaskInstance?.Task?.title}</Text>

                {currentTaskInstance?.Task?.description && (
                    <Text style={styles.heroDescription}>{currentTaskInstance?.Task?.description}</Text>
                )}

                <View style={styles.statusBadgeContainer}>
                    {currentUserAssignment && !isCancelled ? (
                        <View style={styles.statusBadge(currentUserStatusConfig.colorVariant)}>
                            <Unicon name={currentUserStatusConfig.icon} size={18} colorVariant={currentUserStatusConfig.textColorVariant} />
                            <Text style={styles.statusBadgeText(currentUserStatusConfig.textColorVariant)}>{currentUserStatusConfig.label}</Text>
                        </View>
                    ) : (
                        <View style={styles.statusBadge(statusConfig.colorVariant)}>
                            <Unicon name={statusConfig.icon} size={18} colorVariant={statusConfig.textColorVariant} />
                            <Text style={styles.statusBadgeText(statusConfig.textColorVariant)}>{statusConfig.label}</Text>
                        </View>
                    )}
                </View>

                {taskDate && (
                    <>
                        <View style={styles.heroDivider} />
                        <View style={styles.heroDateRow}>
                            <Unicon name="calendar-outline" size={18} colorVariant="textSecondary" />
                            <Text style={styles.heroDateText}>
                                {taskDate.toLocaleString('default', { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                            </Text>
                        </View>
                        <View style={styles.heroTimeRow}>
                            <Unicon name="time-outline" size={18} colorVariant="textSecondary" />
                            <Text style={styles.heroTimeText}>
                                {taskDate.toLocaleString('default', { hour: "numeric", minute: "2-digit", hour12: true })}
                            </Text>
                        </View>
                    </>
                )}

                {currentUserAssignment && !isCancelled && (
                    <View style={styles.actionButtonsRow}>
                        {isApprovalTask ? (
                            // REQUEST_APPROVAL: the approver's decision resolves
                            // the automation step — no accept/complete flow.
                            approvalOutcome ? (
                                <View style={styles.approvalOutcomeRow}>
                                    <Chip
                                        testID="ApprovalOutcomeChip"
                                        colorVariant={approvalOutcome === 'APPROVED' ? 'success' : 'error'}
                                        icon={approvalOutcome === 'APPROVED' ? 'checkmark-circle' : 'close-circle'}
                                        iconSize={14}
                                    >
                                        {approvalOutcome === 'APPROVED' ? 'Approved' : approvalOutcome === 'DENIED' ? 'Denied' : approvalOutcome}
                                    </Chip>
                                    {(approvalDeciderName || approvalDecidedAt) && (
                                        <Text style={styles.approvalDeciderText}>
                                            {approvalDeciderName ? `by ${approvalDeciderName}` : ''}
                                            {approvalDecidedAt ? `${approvalDeciderName ? ' · ' : ''}${approvalDecidedAt}` : ''}
                                        </Text>
                                    )}
                                </View>
                            ) : (
                                <>
                                    <View style={styles.actionButton}>
                                        <SolidButton text="Approve" type="success" onPress={handleApprove} processing={isDeciding} disabled={isActionPending} testID="ApprovalApproveButton" />
                                    </View>
                                    <View style={styles.actionButton}>
                                        <SolidButton text="Deny" type="error" onPress={() => setDenyModalOpen(true)} disabled={isActionPending} testID="ApprovalDenyButton" />
                                    </View>
                                </>
                            )
                        ) : currentTaskInstance?.completed || careTask?.completed ? (
                            // Care work has no reopen path — completed is final
                            // (a wrong completion is corrected at the touch).
                            !isCareTask && (
                                <SolidButton text="Mark as Incomplete" type="border" onPress={() => updateCompletion(false)} processing={isCompleting} disabled={isActionPending} />
                            )
                        ) : (
                            <>
                                {currentUserAssignment.assignmentResponse === "ACCEPTED" && (
                                    <View style={styles.actionButton}>
                                        {isCareTask ? (
                                            <SolidButton
                                                text={{ OUTREACH: 'Complete & Log Result', MEAL: 'Mark Delivered', APPROVAL: 'Approve', OTHER: 'Complete' }[careTask?.purpose] || 'Complete'}
                                                type="success"
                                                onPress={handleCareComplete}
                                                processing={isCompleting}
                                                disabled={isActionPending}
                                                testID="CareTaskCompleteButton"
                                            />
                                        ) : (
                                            <SolidButton text="Complete" type="success" onPress={() => updateCompletion(true)} processing={isCompleting} disabled={isActionPending} testID="CompleteTaskButton" />
                                        )}
                                    </View>
                                )}
                                {(!currentUserAssignment.assignmentResponse || currentUserAssignment.assignmentResponse === "NO_RESPONSE" || currentUserAssignment.assignmentResponse === "DECLINED") && (
                                    <View style={styles.actionButton}>
                                        <SolidButton text="Accept" type="success" onPress={() => updateAssignment(currentUserAssignment.id, "ACCEPTED")} processing={isResponding} disabled={isActionPending} testID="AcceptTaskButton" />
                                    </View>
                                )}
                                {(!currentUserAssignment.assignmentResponse || currentUserAssignment.assignmentResponse === "NO_RESPONSE" || currentUserAssignment.assignmentResponse === "ACCEPTED") && (
                                    <View style={styles.actionButton}>
                                        <SolidButton text={currentUserAssignment.assignmentResponse === "ACCEPTED" ? "Change to Decline" : "Decline"} type="error" onPress={() => setDeclineModalOpen(true)} disabled={isActionPending} testID="DeclineTaskButton" />
                                    </View>
                                )}
                            </>
                        )}
                    </View>
                )}
            </Animated.View>

            {isCancelled && (
                <Callout
                    testID="TaskCancelledBanner"
                    type="warning"
                    title="Cancelled"
                    message="This task was cancelled and no longer needs to be done."
                    style={styles.cancelledCallout}
                />
            )}

            {isCareTask && (
                <Animated.View
                    entering={FadeInDown.delay(200).duration(400)}
                    style={styles.enrolleeCard}
                    testID="CareTaskContextCard"
                >
                    <TouchableOpacity
                        style={styles.enrolleeHeader}
                        activeOpacity={0.7}
                        // Only the Follow Up team gets a link into the care
                        // surface — helpers see the name, nothing else.
                        disabled={!(canViewFollowUp && careTask?.personID)}
                        onPress={() => router.push(`/people/${careTask.personID}?tab=followup`)}
                        testID="CareTaskPersonLink"
                    >
                        <View style={styles.stepIconWrapper}>
                            <Unicon name={getTaskPurposeIcon(careTask?.purpose)} size={22} colorVariant="paths" />
                        </View>
                        <View style={styles.enrolleeIdentity}>
                            <Text style={styles.stepEyebrow}>FOLLOW UP · {getTaskPurposeLabel(careTask?.purpose).toUpperCase()}</Text>
                            <Text style={styles.enrolleeName}>{careTask?.personName || 'Care task'}</Text>
                        </View>
                        {canViewFollowUp && !!careTask?.personID && (
                            <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                        )}
                    </TouchableOpacity>
                    {!!careTask?.safeLogistics && (
                        <Text style={styles.stepDescription} testID="CareTaskLogistics">{careTask.safeLogistics}</Text>
                    )}
                    {!!careTask?.hasContactWarning && (
                        <Callout
                            testID="CareTaskContactWarning"
                            type="warning"
                            title="Contact warning"
                            // Team members can review details on the care
                            // surface; helpers get only the caution itself.
                            message={canViewFollowUp
                                ? 'This person has a contact warning. Review it on their Follow Up page before reaching out.'
                                : 'A contact caution is on file for this person. Coordinate with the Follow Up team.'}
                            style={styles.careWarningCallout}
                        />
                    )}
                </Animated.View>
            )}

            {hasAutomationContext && pathStep && (
                <Animated.View
                    entering={FadeInDown.delay(200).duration(400)}
                    style={styles.enrolleeCard}
                    testID="PathTaskStepCard"
                >
                    <TouchableOpacity
                        style={styles.enrolleeHeader}
                        activeOpacity={0.7}
                        disabled={!(canViewPath && pathInfo?.id)}
                        onPress={() => router.push(`/automations/${pathInfo?.id}`)}
                    >
                        <View style={styles.stepIconWrapper}>
                            <Unicon name="git-branch-outline" size={22} colorVariant="paths" />
                        </View>
                        <View style={styles.enrolleeIdentity}>
                            <Text style={styles.stepEyebrow}>AUTOMATION STEP{pathInfo?.name ? ` — ${pathInfo.name}` : ''}</Text>
                            <Text style={styles.enrolleeName}>{pathStep.name}</Text>
                        </View>
                        {canViewPath && !!pathInfo?.id && (
                            <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                        )}
                    </TouchableOpacity>
                    {hasDisplayValue(pathStep.description) && (
                        <Text style={styles.stepDescription}>{pathStep.description}</Text>
                    )}
                </Animated.View>
            )}

            {hasAutomationContext && enrolleeProfile && (
                <Animated.View
                    entering={FadeInDown.delay(250).duration(400)}
                    style={styles.enrolleeCard}
                    testID="PathTaskEnrolleeCard"
                >
                    <View style={styles.enrolleeHeader}>
                        <Avatar size={52} profile={enrolleeProfile} disableBorder />
                        <View style={styles.enrolleeIdentity}>
                            <Text style={styles.enrolleeEyebrow}>AUTOMATION ENROLLEE</Text>
                            <Text style={styles.enrolleeName}>{enrolleeProfile.firstName} {enrolleeProfile.lastName}</Text>
                        </View>
                        <Unicon name="person-circle-outline" size={24} colorVariant="tasks" />
                    </View>
                    {enrolleeFields.length > 0 && (
                        <View style={styles.enrolleeFields}>
                            {enrolleeFields.map((field, index) => (
                                <View key={`${field.label}-${index}`} style={styles.enrolleeField}>
                                    <Text style={styles.enrolleeFieldLabel}>{field.label}</Text>
                                    <Text selectable style={styles.enrolleeFieldValue}>{field.value}</Text>
                                </View>
                            ))}
                        </View>
                    )}
                </Animated.View>
            )}

            {enrollmentContext?.PathStep?.config?.taskSource === 'PREDEFINED' && enrolleeProfile && (() => {
                const config = enrollmentContext?.PathStep?.config || {};
                const phones = (enrolleeProfile.Phones || []).filter(p => !p.private);
                const phone = phones[0]?.phoneNumber?.replace(/ /g, '') || null;
                const email = enrolleeProfile.email || null;
                const firstName = enrolleeProfile.firstName || '';
                const lastName = enrolleeProfile.lastName || '';
                const fullName = `${firstName} ${lastName}`.trim();
                const interp = (text) => (text || '')
                    .replace(/\{\{firstName\}\}/gi, firstName)
                    .replace(/\{\{lastName\}\}/gi, lastName)
                    .replace(/\{\{fullName\}\}/gi, fullName);
                const openURL = (url) => Linking.openURL(url).catch(() => Toast.show({ type: 'error', text2: 'Could not open the app.' }));
                const typeDef = PREDEFINED_TASK_TYPES[config.predefinedType];
                return (
                    <Animated.View entering={FadeInDown.delay(200).duration(400)} style={styles.heroCard}>
                        <Text style={styles.heroTitle}>{typeDef?.label || config.predefinedType || 'Predefined Task'}</Text>
                        {config.predefinedType === 'PERSONAL_SMS' && config.message && (
                            <Text style={styles.heroDescription}>{interp(config.message)}</Text>
                        )}
                        {config.predefinedType === 'PERSONAL_PHONE_CALL' && config.callNotes && (
                            <Text style={styles.heroDescription}>{interp(config.callNotes)}</Text>
                        )}
                        {config.predefinedType === 'PERSONAL_EMAIL' && (
                            <>
                                {config.subject && <Text style={styles.heroDescription}>Subject: {interp(config.subject)}</Text>}
                                {config.body && <Text style={styles.heroDescription}>{interp(config.body)}</Text>}
                            </>
                        )}
                        <View style={styles.actionButtonsRow}>
                            {config.predefinedType === 'PERSONAL_SMS' && phone && (
                                <View style={styles.actionButton}>
                                    <SolidButton text="Open in Messages" type="success" onPress={() => openURL(`sms:${phone}?body=${encodeURIComponent(interp(config.message || ''))}`)} testID="predefined-open-sms" />
                                </View>
                            )}
                            {config.predefinedType === 'PERSONAL_PHONE_CALL' && phone && (
                                <View style={styles.actionButton}>
                                    <SolidButton text="Call" type="success" onPress={() => openURL(`tel:${phone}`)} testID="predefined-open-call" />
                                </View>
                            )}
                            {config.predefinedType === 'PERSONAL_EMAIL' && email && (
                                <View style={styles.actionButton}>
                                    <SolidButton text="Open Email" type="success" onPress={() => openURL(`mailto:${email}?subject=${encodeURIComponent(interp(config.subject || ''))}&body=${encodeURIComponent(interp(config.body || ''))}`)} testID="predefined-open-email" />
                                </View>
                            )}
                        </View>
                    </Animated.View>
                );
            })()}

            {/* Navigation Links */}
            <Animated.View entering={FadeInDown.delay(300).duration(400)} style={styles.navigationLinks}>
                <TouchableOpacity
                    style={styles.navLink}
                    onPress={() => router.push("/tasks/" + currentTaskInstance?.Task?.id)}
                    activeOpacity={0.7}
                >
                    <View style={styles.navLinkIconWrapper}>
                        <Unicon name="clipboard-outline" size={20} colorVariant="primary" />
                    </View>
                    <View style={styles.navLinkContent}>
                        <Text style={styles.navLinkLabel}>Parent Task</Text>
                        <Text style={styles.navLinkTitle} numberOfLines={1}>{currentTaskInstance?.Task?.title}</Text>
                    </View>
                    <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                </TouchableOpacity>

                {eventName && (
                    <TouchableOpacity
                        style={styles.navLink}
                        onPress={() => router.push("/events/instances/" + currentTaskInstance?.EventInstance?.id)}
                        activeOpacity={0.7}
                    >
                        <View style={[styles.navLinkIconWrapper, { backgroundColor: eventColor }]}>
                            <Unicon name="calendar-outline" size={20} style={{ color: getAccessibleTextColor(eventColor) }} />
                        </View>
                        <View style={styles.navLinkContent}>
                            <Text style={styles.navLinkLabel}>Event</Text>
                            <Text style={styles.navLinkTitle} numberOfLines={1}>{eventName}</Text>
                        </View>
                        <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                    </TouchableOpacity>
                )}

                    {/* Follow Up surface link — team only; helpers never get
                    navigation into the care record (spec §8.4). */}
                {isCareTask && canViewFollowUp && !!careTask?.personID && (
                    <TouchableOpacity
                        style={styles.navLink}
                        onPress={() => router.push(`/people/${careTask.personID}?tab=followup`)}
                        activeOpacity={0.7}
                        testID="CareTaskFollowUpNavLink"
                    >
                        <View style={styles.navLinkIconWrapper}>
                            <Unicon name="heart-outline" size={20} colorVariant="primary" />
                        </View>
                        <View style={styles.navLinkContent}>
                            <Text style={styles.navLinkLabel}>Follow Up</Text>
                            <Text style={styles.navLinkTitle} numberOfLines={1}>{careTask?.personName || 'Person care surface'}</Text>
                        </View>
                        <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                    </TouchableOpacity>
                )}

                {currentTaskInstance?.Task?.dueType === "TRAINING" && trainingClassInstance && (
                    <TouchableOpacity
                        style={styles.navLink}
                        onPress={() => router.push(`/training/${trainingClassInstance?.Class?.Module?.id}/instance/${trainingClassInstance?.id}`)}
                        activeOpacity={0.7}
                        testID="TrainingClassNavLink"
                    >
                        <View style={styles.navLinkIconWrapper}>
                            <Unicon name="school-outline" size={20} colorVariant="primary" />
                        </View>
                        <View style={styles.navLinkContent}>
                            <Text style={styles.navLinkLabel}>Training Class</Text>
                            <Text style={styles.navLinkTitle} numberOfLines={1}>{trainingClassInstance?.Class?.name}</Text>
                            {trainingClassInstance?.scheduledAt && (
                                <Text style={styles.navLinkSubtitle} numberOfLines={1}>
                                    {new Date(trainingClassInstance?.scheduledAt).toLocaleString('default', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </Text>
                            )}
                        </View>
                        <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                    </TouchableOpacity>
                )}
            </Animated.View>

            {/* Self Assignment */}
            {!currentUserAssignment && currentTaskInstance?.Task?.allowSelfAssignment && (
                <Animated.View entering={FadeInDown.delay(400).duration(400)} style={styles.selfAssignCard}>
                    <Text style={styles.selfAssignText}>This task allows self-assignment</Text>
                    <SolidButton
                        type="primary"
                        size="sm"
                        text="Assign to Me"
                        processing={isAssigning}
                        disabled={isActionPending}
                        onPress={() => {
                            updateAssigneesMutation.mutate({
                                taskInstanceID: currentTaskInstance?.id,
                                existingAssignees: currentTaskInstance?.Assignees,
                                newAssignees: [loggedInUser],
                                taskInstance: currentTaskInstance,
                                currentUser: loggedInUser?.id,
                                assignmentResponse: "ACCEPTED"
                            });
                        }}
                    />
                </Animated.View>
            )}

            {/* Info Cards */}
            <Animated.View entering={FadeInDown.delay(400).duration(400)} style={styles.infoGrid}>
                <View style={styles.infoCard}>
                    <View style={styles.infoIconWrapper}>
                        <Unicon name="layers-outline" size={20} colorVariant="primary" />
                    </View>
                    <Text style={styles.infoLabel}>Task Type</Text>
                    <Text style={styles.infoValue}>{getTaskTypeLabel(currentTaskInstance?.Task?.dueType)}</Text>
                </View>

                <View style={styles.infoCard}>
                    <View style={styles.infoIconWrapper}>
                        <Unicon name="people-outline" size={20} colorVariant="info" />
                    </View>
                    <Text style={styles.infoLabel}>Assignees</Text>
                    <Text style={styles.infoValue}>{sortedAssignees.length}/{getAssigneesNeeded(currentTaskInstance)} Assignees</Text>
                </View>
            </Animated.View>

            {/* Assignees Section */}
            <Animated.View entering={FadeInDown.delay(500).duration(400)} style={styles.assigneesSection}>
                <View style={styles.sectionHeader}>
                    <SectionTitle title="Assignees" containerStyle={styles.sectionTitleContainer} />
                    <View style={styles.assigneesBadge}>
                        <Text style={styles.assigneesBadgeText}>{sortedAssignees.length}</Text>
                    </View>
                </View>

                {sortedAssignees.length === 0 ? (
                    <EmptyListPlaceholder icon="people" spacing={20} message="This task currently does not have any assignees" />
                ) : (
                    <View style={styles.assigneesList}>
                        {sortedAssignees.map((assignee, index) => {
                            const conflictingTasks = assignee.conflicts?.map(c => c.Task?.title);
                            const isAssigner = currentTaskInstance?.VolunteerTeam?.VolunteerTeamAssigners?.find(a => a.personID === loggedInUser?.id);

                            return (
                                <Animated.View
                                    key={assignee.id}
                                    entering={FadeInUp.delay(550 + index * 50).duration(300)}
                                    layout={Layout.springify()}
                                    style={[styles.assigneeItem, { opacity: assignee.assignmentResponse === "ACCEPTED" ? 1 : 0.85 }]}
                                >
                                    <ProfileCard
                                        minimal
                                        profile={assignee}
                                        selected
                                        selectedIcon={!assignee.assignmentResponse || assignee.assignmentResponse === "NO_RESPONSE" ? "help-circle" : assignee.assignmentResponse === "ACCEPTED" ? "checkmark-circle" : "close-circle"}
                                        selectedColorVariant={!assignee.assignmentResponse || assignee.assignmentResponse === "NO_RESPONSE" ? "warning" : assignee.assignmentResponse === "ACCEPTED" ? "success" : "error"}
                                    />
                                    {assignee.assignmentResponse === "DECLINED" && (isAssigner || loggedInUser?.isAdmin) && (
                                        <View style={styles.declinedReason}>
                                            <Text style={styles.declinedLabel}>Declined:</Text>
                                            <Text style={styles.declinedText}>{assignee?.reason}</Text>
                                        </View>
                                    )}
                                    {assignee.conflicts && assignee.conflicts?.length > 0 && (
                                        <View style={styles.conflictRow}>
                                            <Unicon name="warning" size={16} colorVariant="error" />
                                            <Text style={styles.conflictText}>{`${assignee?.firstName} is also scheduled for: ${conflictingTasks.join(", ")}`}</Text>
                                        </View>
                                    )}
                                </Animated.View>
                            );
                        })}
                    </View>
                )}
            </Animated.View>
        </Animated.View>
    );

    if (taskNotFound || (!isLoadingTaskInstance && (!currentTaskInstance?.id || currentTaskInstance?.isDeleted))) return renderNotFound();

    return (
        <View style={styles.container}>
            <ScrollView
                keyboardShouldPersistTaps="handled"
                ref={scroll_ref}
                contentContainerStyle={styles.scrollContent}
                scrollEventThrottle={16}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={handleRefresh}
                    />
                }
            >
                <View style={styles.maxWidthContainer}>
                    {isLoadingTaskInstance ? renderLoadingState() : renderContent()}
                </View>
            </ScrollView>

            <SheetModal
                isOpen={declineModalOpen}
                onClose={() => setDeclineModalOpen(false)}
                title="Decline Task"
                stayOpenOnBlur
                snapPoints={[300, 520]}
                loading={updateAssigneeResponseMutation.isPending}
                confirmText="Confirm"
                onConfirm={() => {
                    if (!currentUserAssignment?.id) {
                        setDeclineModalOpen(false);
                        return;
                    }
                    updateAssignment(currentUserAssignment.id, "DECLINED", currentUserAssignment?.reason, currentUserAssignment?.forwardToID);
                    setDeclineModalOpen(false);
                }}
            >
                <InputSection>
                    <Input
                        title="Reason/Comment"
                        helpText="Optionally provide a reason for declining this task - this helps schedulers and team leaders improve future scheduling."
                        value={currentUserAssignment?.reason}
                        onUpdate={(new_value) => {
                            setCurrentUserAssignment({ ...currentUserAssignment, reason: new_value });
                        }}
                    />
                    {currentTaskInstance?.Task?.allowForwarding && (
                        <View>
                            <SheetSelector
                                addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null}
                                type="people"
                                name="Forward Task"
                                fromScreen={pathname}
                                value={currentUserAssignment?.forwardTo}
                                excludeIds={[loggedInUser?.id]}
                                errorFn={(item) => {
                                    return currentTaskInstance?.Assignees?.map(a => a.id).includes(item.id) ? ["Already Assigned"] : [];
                                }}
                                onChange={(selected_item) => {
                                    setCurrentUserAssignment({
                                        ...currentUserAssignment,
                                        forwardToID: selected_item?.id,
                                        forwardTo: selected_item
                                    });
                                }}
                            />
                            <Text style={styles.forwardHelpText}>For this task, you are allowed to forward assignment to someone else.</Text>
                        </View>
                    )}
                </InputSection>
            </SheetModal>

            {/* REQUEST_APPROVAL deny sheet — reason is optional and becomes
                the RPC p_note. */}
            <SheetModal
                isOpen={denyModalOpen}
                onClose={() => setDenyModalOpen(false)}
                title="Deny Approval"
                stayOpenOnBlur
                snapPoints={[300, 520]}
                loading={recordApprovalMutation.isPending}
                confirmText="Deny"
                onConfirm={handleDeny}
            >
                <InputSection>
                    <Input
                        title="Reason (optional)"
                        helpText="Optionally explain why you're denying — it's recorded with the decision."
                        value={denyReason}
                        onUpdate={setDenyReason}
                        multiline
                        testID="ApprovalDenyReasonInput"
                    />
                </InputSection>
            </SheetModal>

            {/* Care-task completion: structured touch (OUTREACH) or optional
                touch (OTHER) via follow_up_complete_task — never a direct
                TaskInstances.completed write. */}
            <CompleteCareTaskSheet
                isOpen={careSheetOpen}
                onClose={() => setCareSheetOpen(false)}
                organizationID={loggedInUser?.organizationID}
                taskInstanceID={currentTaskInstance?.id}
                personID={careTask?.personID}
                personName={careTask?.personName}
                purpose={careTask?.purpose || 'OTHER'}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    scrollContent: {
        paddingBottom: 120,
        alignItems: "center",
    },
    maxWidthContainer: {
        maxWidth: 700,
        width: "100%",
    },
    headerTitleContainer: {
        alignItems: "center",
        justifyContent: "center",
    },
    headerTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: "center",
    },
    headerSubtitle: {
        fontSize: 12,
        fontStyle: "italic",
        color: colors.textSecondary,
        textAlign: "center",
    },
    headerRight: {
        flexDirection: "row",
        marginTop: -2,
    },
    loadingContainer: {
        padding: 16,
        gap: 12,
    },
    loadingRow: {
        flexDirection: "row",
        gap: 12,
    },
    loadingHalf: {
        flex: 1,
    },
    notFoundContainer: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
    },
    notFoundTitle: {
        fontSize: 24,
        fontFamily: fonts.heading,
        color: colors.text,
        marginTop: 20,
    },
    notFoundSubtitle: {
        fontSize: 16,
        color: colors.textSecondary,
        textAlign: "center",
        marginTop: 12,
        lineHeight: 24,
    },
    contentContainer: {
        padding: 16,
    },
    enrolleeCard: {
        backgroundColor: colors.surface,
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.06)",
    },
    enrolleeHeader: {
        flexDirection: "row",
        alignItems: "center",
    },
    enrolleeIdentity: {
        flex: 1,
        marginLeft: 12,
    },
    enrolleeEyebrow: {
        color: colors.tasks,
        fontSize: 10,
        fontFamily: fonts.bold,
        letterSpacing: 0.8,
        marginBottom: 2,
    },
    enrolleeName: {
        color: colors.text,
        fontSize: 17,
        fontFamily: fonts.heading,
    },
    enrolleeFields: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 10,
        marginTop: 14,
        paddingTop: 14,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    enrolleeField: {
        minWidth: 140,
        flexBasis: "47%",
        flexGrow: 1,
    },
    enrolleeFieldLabel: {
        color: colors.textSecondary,
        fontSize: 10,
        fontFamily: fonts.bold,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        marginBottom: 2,
    },
    enrolleeFieldValue: {
        color: colors.text,
        fontSize: 13,
        lineHeight: 18,
    },
    stepIconWrapper: {
        width: 52,
        height: 52,
        borderRadius: 14,
        backgroundColor: colors.pathsContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    stepEyebrow: {
        color: colors.paths,
        fontSize: 10,
        fontFamily: fonts.bold,
        letterSpacing: 0.8,
        marginBottom: 2,
    },
    stepDescription: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        fontSize: 13,
        color: colors.textSecondary,
        lineHeight: 18,
    },
    navigationLinks: {
        gap: 8,
        marginBottom: 16,
    },
    navLink: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: colors.surface,
        borderRadius: 14,
        padding: 14,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 4,
        elevation: 2,
    },
    navLinkIconWrapper: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: colors.primaryContainer,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 12,
    },
    navLinkContent: {
        flex: 1,
    },
    navLinkLabel: {
        fontSize: 11,
        color: colors.textSecondary,
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 2,
    },
    navLinkTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    navLinkSubtitle: {
        fontSize: 11,
        color: colors.textSecondary,
        marginTop: 2,
    },
    eventDot: {
        width: 12,
        height: 12,
        borderRadius: 6,
    },
    heroCard: {
        backgroundColor: colors.surface,
        borderRadius: 20,
        padding: 24,
        alignItems: "center",
        marginBottom: 16,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 3,
    },
    statusBadgeContainer: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: 10,
        marginTop: 8,
    },
    statusBadge: (colorVariant) => ({
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 14,
        paddingVertical: 6,
        borderRadius: 20,
        borderWidth: 1,
        gap: 6,
        marginBottom: 16,
        borderColor: colors[colorVariant],
        backgroundColor: colors[colorVariant],
    }),
    statusBadgeText: (textColorVariant) => ({
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors[textColorVariant],
    }),
    heroTitle: {
        fontSize: 22,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: "center",
        marginBottom: 8,
    },
    heroDescription: {
        fontSize: 15,
        color: colors.textSecondary,
        textAlign: "center",
        lineHeight: 22,
    },
    heroDivider: {
        width: 40,
        height: 2,
        backgroundColor: colors.border,
        borderRadius: 1,
        marginVertical: 20,
    },
    heroDateRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 6,
    },
    heroDateText: {
        fontSize: 15,
        color: colors.text,
        fontFamily: fonts.heading,
    },
    heroTimeRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    heroTimeText: {
        fontSize: 14,
        color: colors.textSecondary,
    },
    assignmentCard: {
        backgroundColor: colors.surface,
        borderRadius: 16,
        padding: 20,
        marginBottom: 16,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 4,
        elevation: 2,
    },
    actionButtonsRow: {
        flexDirection: "row",
        justifyContent: "space-around",
        alignItems: "center",
        paddingTop: 16,
        gap: 12,
    },
    actionButton: {
        flex: 1,
    },
    approvalOutcomeRow: {
        flex: 1,
        alignItems: "center",
        gap: 6,
    },
    approvalDeciderText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    selfAssignCard: {
        backgroundColor: colors.surface,
        borderRadius: 16,
        padding: 20,
        marginBottom: 16,
        alignItems: "center",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 4,
        elevation: 2,
    },
    selfAssignText: {
        fontSize: 14,
        color: colors.textSecondary,
        marginBottom: 16,
    },
    infoGrid: {
        flexDirection: "row",
        gap: 12,
        marginBottom: 16,
    },
    infoCard: {
        flex: 1,
        backgroundColor: colors.surface,
        borderRadius: 16,
        padding: 16,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 4,
        elevation: 2,
    },
    infoIconWrapper: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: colors.primaryContainer,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 12,
    },
    infoLabel: {
        fontSize: 12,
        color: colors.textSecondary,
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 4,
    },
    infoValue: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    assigneesSection: {
        marginTop: 8,
    },
    sectionHeader: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 12,
    },
    sectionTitleContainer: {
        marginLeft: 4,
    },
    assigneesBadge: {
        backgroundColor: colors.primaryContainer,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        marginLeft: 8,
    },
    assigneesBadgeText: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.primary,
    },
    assigneesList: {
        gap: 8,
    },
    assigneeItem: {
        marginBottom: 4,
    },
    declinedReason: {
        backgroundColor: colors.surface,
        padding: 12,
        marginTop: -8,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
    },
    declinedLabel: {
        color: colors.error,
        fontSize: 13,
        fontWeight: "bold",
        marginBottom: 4,
    },
    declinedText: {
        color: colors.text,
        fontSize: 13,
    },
    conflictRow: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 8,
        paddingHorizontal: 4,
    },
    conflictText: {
        color: colors.error,
        fontStyle: "italic",
        paddingLeft: 8,
        fontSize: 13,
        flex: 1,
    },
    forwardHelpText: {
        color: colors.textSecondary,
        marginTop: 8,
        fontSize: 12,
        fontStyle: "italic",
    },
    cancelledCallout: {
        marginBottom: 16,
    },
    careWarningCallout: {
        marginTop: 12,
    },
}));

export default TaskInstanceDetailsScreen;